import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Col,
	DatePicker,
	Empty,
	InputNumber,
	Row,
	Select,
	Space,
	Spin,
	Typography,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSearchParams } from 'react-router-dom';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip as ChartTooltip,
	XAxis,
	YAxis,
} from 'recharts';

import Header from '../../../../components/Header';
import { useGetBuildingsQuery } from '../../../../redux/api/location';
import {
	CycleSection,
	MoneyBlock,
	MoneyMixBucket,
	useGetShopPerformanceFiltersQuery,
	useGetShopPerformanceQuery,
} from '../../../../redux/api/shop-performance-report';
import { deltaClass, deltaCopy, formatDollars, formatPct } from './format';
import { HISTOGRAM_BUCKETS } from './histogramBuckets';
import PrintableReport from './PrintableReport';
import {
	ShopPerformanceFilters,
	dateWindowInvalid,
	filtersEqual,
	filtersFromSearchParams,
	filtersToSearchParams,
	invertedRange,
} from './queryParams';
import './print.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;
const { Text } = Typography;

const CHICAGO = 'America/Chicago';
const PRINT_MODE_CLASS = 'shop-performance-print-mode';
const PRINT_PAGE_RULE = '@page { size: portrait; margin: 0.5in; }';

const AVERAGE_COPY: Array<{
	key: keyof CycleSection['averages'];
	label: string;
	hint: string;
}> = [
	{
		key: 'acceptance',
		label: 'Acceptance',
		hint: 'Received to PB Prod Accepted',
	},
	{
		key: 'material_prep',
		label: 'Material prep',
		hint: 'Received to material prep completed',
	},
	{
		key: 'kitting',
		label: 'Kitting',
		hint: 'Received to kitting completed',
	},
	{
		key: 'machine_setup',
		label: 'Machine setup',
		hint: 'Received to machine setup started',
	},
	{
		key: 'machining_complete',
		label: 'Machining complete',
		hint: 'Received to machining complete',
	},
	{
		key: 'delivery',
		label: 'Delivery',
		hint: 'Received to first delivered-in-full',
	},
	{
		key: 'lead_time',
		label: 'Planned lead time',
		hint: 'Received to required ship date',
	},
];

const BAR_COLOR: Record<string, string> = {
	early: '#3f8600',
	on_time: '#1677ff',
	late: '#cf1322',
};

const defaultDates = () => {
	const today = dayjs().tz(CHICAGO).startOf('day');
	return {
		dateFrom: today.subtract(29, 'day').format('YYYY-MM-DD'),
		dateTo: today.format('YYYY-MM-DD'),
	};
};

const emptyFilters = (): ShopPerformanceFilters => ({
	...defaultDates(),
	methods: [],
	buildings: [],
	materials: [],
	finishes: [],
	coatings: [],
});

const toRange = (filters: ShopPerformanceFilters): [Dayjs, Dayjs] => [
	dayjs.tz(filters.dateFrom, CHICAGO),
	dayjs.tz(filters.dateTo, CHICAGO),
];

const formatPriorRange = (from?: string, to?: string) => {
	if (!from || !to) return '';
	return `${dayjs(from).format('MMM D')} – ${dayjs(to).format('MMM D')}`;
};

type Option = { value: string; label: string };

const SearchableMulti = ({
	value,
	onChange,
	options,
	placeholder,
}: {
	value: string[];
	onChange: (next: string[]) => void;
	options: Option[];
	placeholder: string;
}) => {
	const [search, setSearch] = useState('');
	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return options;
		return options.filter((option) => option.label.toLowerCase().includes(needle));
	}, [options, search]);

	return (
		<Select
			mode="multiple"
			allowClear
			showSearch
			maxTagCount="responsive"
			placeholder={placeholder}
			value={value}
			options={options}
			onChange={onChange}
			onSearch={setSearch}
			className="w-full"
			filterOption={(input, option) =>
				String(option?.label ?? '')
					.toLowerCase()
					.includes(input.toLowerCase())
			}
			dropdownRender={(menu) => (
				<>
					<Space
						style={{ padding: 8 }}
						onMouseDown={(event) => event.preventDefault()}
					>
						<Button
							size="small"
							onMouseDown={(event) => {
								event.preventDefault();
								const extra = filtered.map((option) => option.value);
								onChange(Array.from(new Set([...value, ...extra])));
							}}
						>
							Select all
						</Button>
						<Button
							size="small"
							onMouseDown={(event) => {
								event.preventDefault();
								const drop = new Set(filtered.map((option) => option.value));
								onChange(value.filter((item) => !drop.has(item)));
							}}
						>
							Deselect all
						</Button>
					</Space>
					{menu}
				</>
			)}
		/>
	);
};

const FieldLabel = ({
	children,
	hint,
}: {
	children: string;
	hint?: string;
}) => (
	<div className="mb-1 flex items-baseline justify-between gap-2">
		<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
			{children}
		</span>
		{hint ? <span className="text-[11px] text-slate-400">{hint}</span> : null}
	</div>
);

const BoundPair = ({
	min,
	max,
	onMin,
	onMax,
	error,
}: {
	min?: number;
	max?: number;
	onMin: (value: number | undefined) => void;
	onMax: (value: number | undefined) => void;
	error?: boolean;
}) => {
	const toInt = (value: number | null) =>
		value === null ? undefined : Math.trunc(value);

	return (
		<Space.Compact className="w-full">
			<InputNumber
				className="w-full"
				min={0}
				precision={0}
				step={1}
				placeholder="Min"
				status={error ? 'error' : undefined}
				value={min}
				onChange={(value) => onMin(toInt(value))}
			/>
			<InputNumber
				className="w-full"
				min={0}
				precision={0}
				step={1}
				placeholder="Max"
				status={error ? 'error' : undefined}
				value={max}
				onChange={(value) => onMax(toInt(value))}
			/>
		</Space.Compact>
	);
};

type QuantityPoint = {
	key: string;
	label: string;
	lines: number;
	dollars: number;
};

const QuantityTooltip = ({
	active,
	payload,
	showMoney,
}: {
	active?: boolean;
	payload?: Array<{ payload: QuantityPoint }>;
	showMoney: boolean;
}) => {
	if (!active || !payload?.length) return null;
	const row = payload[0].payload;
	return (
		<div className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-sm">
			<div className="font-medium">{row.label} pcs</div>
			<div className="tabular-nums text-slate-600">
				{showMoney
					? `${formatDollars(String(row.dollars))} · ${row.lines} lines`
					: `${row.lines} lines`}
			</div>
		</div>
	);
};

const QuantityMixChart = ({
	buckets,
	showMoney,
	moneyLabel,
}: {
	buckets: MoneyMixBucket[];
	showMoney: boolean;
	moneyLabel: string;
}) => {
	const data: QuantityPoint[] = buckets
		.filter((bucket) => bucket.key !== 'unknown' || bucket.lines > 0)
		.map((bucket) => ({
			key: bucket.key,
			label: bucket.label,
			lines: bucket.lines,
			dollars: Math.round(Number(bucket.total ?? 0)),
		}));

	return (
		<div>
			<div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
				Quantity mix
			</div>
			<div className="h-56">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="label" interval={0} />
						{showMoney ? (
							<YAxis
								yAxisId="dollars"
								tickFormatter={(value) =>
									`$${Number(value).toLocaleString('en-US')}`
								}
								width={64}
							/>
						) : null}
						<YAxis
							yAxisId="lines"
							orientation="right"
							allowDecimals={false}
							width={36}
						/>
						<ChartTooltip content={<QuantityTooltip showMoney={showMoney} />} />
						<Legend />
						{showMoney ? (
							<Bar
								yAxisId="dollars"
								dataKey="dollars"
								name={moneyLabel}
								fill="#1677ff"
								maxBarSize={36}
							/>
						) : null}
						<Line
							yAxisId="lines"
							dataKey="lines"
							name="Lines"
							type="linear"
							stroke="#1f2937"
							strokeWidth={2}
							dot={{ r: 3 }}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</div>
			<Text type="secondary" className="mt-1 block text-[11px]">
				Bars and line use different scales.
			</Text>
		</div>
	);
};

const MoneyCard = ({
	title,
	block,
	emptyText,
	showMoney,
	moneyLabel,
	costFootnote,
}: {
	title: string;
	block: MoneyBlock;
	emptyText: string;
	showMoney: boolean;
	moneyLabel: string;
	costFootnote?: string;
}) => (
	<Card title={title} styles={{ body: { paddingTop: 16 } }}>
		{block.lines === 0 ? (
			<Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
		) : (
			<>
				<div className="flex flex-wrap items-end justify-between gap-4">
					<div>
						<div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
							Total
						</div>
						<div className="text-3xl font-semibold tabular-nums leading-tight">
							{showMoney ? formatDollars(block.total) : 'Hidden'}
						</div>
					</div>
					<div className="flex gap-6 text-sm">
						<div>
							<div className="text-[11px] uppercase tracking-wide text-slate-500">Orders</div>
							<div className="text-lg tabular-nums">{block.orders}</div>
						</div>
						<div>
							<div className="text-[11px] uppercase tracking-wide text-slate-500">Lines</div>
							<div className="text-lg tabular-nums">{block.lines}</div>
						</div>
						<div>
							<div className="text-[11px] uppercase tracking-wide text-slate-500">
								Material cost
							</div>
							<div className="text-lg tabular-nums">
								{showMoney
									? `${formatDollars(block.material_cost)} · ${formatPct(block.material_cost_pct)}`
									: 'Hidden'}
							</div>
							{costFootnote ? (
								<div className="max-w-[16rem] text-[11px] leading-snug text-slate-400">
									{costFootnote}
								</div>
							) : null}
						</div>
					</div>
				</div>
				<table className="mt-4 w-full text-sm">
					<thead>
						<tr className="text-[11px] uppercase tracking-wide text-slate-500">
							<th className="pb-1 text-left font-semibold">Planned lead time</th>
							<th className="pb-1 text-right font-semibold">Lines</th>
							{showMoney ? (
								<>
									<th className="pb-1 text-right font-semibold">Total</th>
									<th className="pb-1 text-right font-semibold">Line share</th>
								</>
							) : null}
						</tr>
					</thead>
					<tbody>
						{block.by_lead_time.map((bucket) => {
							const share =
								block.lines === 0
									? '0'
									: String(Math.round((bucket.lines / block.lines) * 100));
							return (
								<tr key={bucket.key} className="border-t border-slate-100">
									<td className="py-1.5">{bucket.label}</td>
									<td className="py-1.5 text-right tabular-nums">{bucket.lines}</td>
									{showMoney ? (
										<>
											<td className="py-1.5 text-right tabular-nums">
												{formatDollars(bucket.total)}
											</td>
											<td className="py-1.5 text-right tabular-nums text-slate-500">
												{formatPct(share)}
											</td>
										</>
									) : null}
								</tr>
							);
						})}
					</tbody>
				</table>
				<div className="mt-5">
					<QuantityMixChart
						buckets={block.by_quantity}
						showMoney={showMoney}
						moneyLabel={moneyLabel}
					/>
				</div>
			</>
		)}
	</Card>
);

const CyclePanel = ({
	title,
	emptyText,
	section,
	priorLabel,
}: {
	title: string;
	emptyText: string;
	section: CycleSection;
	priorLabel: string;
}) => {
	const chartData = section.histogram.map((bar) => {
		const meta = HISTOGRAM_BUCKETS.find((item) => item.key === bar.key);
		return {
			...bar,
			side: meta?.side ?? 'on_time',
			percent:
				section.histogram_n === 0
					? 0
					: Math.round((bar.count / section.histogram_n) * 1000) / 10,
		};
	});

	return (
		<Card title={title} styles={{ body: { paddingTop: 16 } }}>
			{section.line_count === 0 ? (
				<Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
			) : (
				<Row gutter={[32, 24]}>
					<Col xs={24} xl={12}>
						<table className="w-full text-sm">
							<thead>
								<tr className="text-[11px] uppercase tracking-wide text-slate-500">
									<th className="pb-2 text-left font-semibold">Stage</th>
									<th className="pb-2 text-right font-semibold">Days</th>
									<th className="pb-2 text-right font-semibold">Lines</th>
									<th className="pb-2 text-right font-semibold">vs prior</th>
								</tr>
							</thead>
							<tbody>
								{AVERAGE_COPY.map((item) => {
									const metric = section.averages[item.key];
									return (
										<tr key={item.key} className="border-t border-slate-100">
											<td className="py-2 pr-3">
												<div className="font-medium">{item.label}</div>
												<div className="text-[11px] text-slate-400">{item.hint}</div>
											</td>
											<td className="py-2 text-right text-lg font-semibold tabular-nums">
												{metric.days ?? '—'}
											</td>
											<td className="py-2 text-right tabular-nums text-slate-600">
												{metric.n}
											</td>
											<td
												className={`py-2 text-right text-[11px] font-medium ${deltaClass(metric.delta_days)}`}
											>
												{deltaCopy(metric.delta_days)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						<Text type="secondary" className="mt-2 block text-[11px]">
							Prior period {priorLabel}. Lower days are better.
						</Text>
					</Col>
					<Col xs={24} xl={12}>
						<div className="mb-3 flex items-end justify-between gap-4">
							<div>
								<div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
									On or before ship date
								</div>
								<div className="text-4xl font-semibold tabular-nums leading-none text-emerald-700">
									{formatPct(section.on_time_pct)}
								</div>
								<Text type="secondary" className="text-xs">
									{section.on_time_n} of {section.histogram_n} delivered lines with a
									ship date
								</Text>
								{section.line_count > section.histogram_n && (
									<Text type="secondary" className="block text-xs">
										{section.line_count - section.histogram_n} more not counted
										&mdash; not yet delivered in full, or no required ship date
									</Text>
								)}
							</div>
						</div>
						<div className="h-64">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="label" interval={0} angle={-30} textAnchor="end" height={56} />
									<YAxis allowDecimals={false} />
									<ChartTooltip
										formatter={(value, _name, props) => {
											const percent = (props.payload as { percent: number }).percent;
											return [`${value} (${percent}%)`, 'Lines'];
										}}
									/>
									<Bar dataKey="count">
										{chartData.map((bar) => (
											<Cell key={bar.key} fill={BAR_COLOR[bar.side]} />
										))}
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</Col>
				</Row>
			)}
		</Card>
	);
};

const ShopPerformance = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [draft, setDraft] = useState<ShopPerformanceFilters>(() =>
		filtersFromSearchParams(searchParams, emptyFilters())
	);
	const [applied, setApplied] = useState<ShopPerformanceFilters>(draft);
	const searchKey = searchParams.toString();

	useEffect(() => {
		const params = new URLSearchParams(searchKey);
		if (!params.get('date_from') || !params.get('date_to')) {
			setSearchParams(filtersToSearchParams(emptyFilters()), { replace: true });
			return;
		}
		const next = filtersFromSearchParams(params, emptyFilters());
		setDraft((prev) => (filtersEqual(prev, next) ? prev : next));
		setApplied((prev) => (filtersEqual(prev, next) ? prev : next));
	}, [searchKey, setSearchParams]);

	const leadError = invertedRange(draft.leadTimeMin, draft.leadTimeMax);
	const qtyError = invertedRange(draft.qtyMin, draft.qtyMax);
	const windowError = dateWindowInvalid(draft.dateFrom, draft.dateTo);
	const formInvalid = leadError || qtyError || windowError;
	const dirty = !filtersEqual(draft, applied);
	const appliedInvalid =
		invertedRange(applied.leadTimeMin, applied.leadTimeMax) ||
		invertedRange(applied.qtyMin, applied.qtyMax) ||
		dateWindowInvalid(applied.dateFrom, applied.dateTo);

	const { data: filterOptions } = useGetShopPerformanceFiltersQuery();
	const { data: buildings } = useGetBuildingsQuery();
	const { data, isFetching, isError, error, refetch } = useGetShopPerformanceQuery(
		applied,
		{ skip: appliedInvalid }
	);

	useEffect(() => {
		if (!data) return;
		const style = document.createElement('style');
		style.media = 'print';
		style.textContent = PRINT_PAGE_RULE;
		document.head.appendChild(style);
		document.body.classList.add(PRINT_MODE_CLASS);
		return () => {
			style.remove();
			document.body.classList.remove(PRINT_MODE_CLASS);
		};
	}, [data]);

	const methodOptions = (filterOptions?.methods ?? []).map((value) => ({
		value,
		label: value,
	}));
	const materialOptions = (filterOptions?.materials ?? []).map((value) => ({
		value,
		label: value,
	}));
	const finishOptions = (filterOptions?.finishes ?? []).map((value) => ({
		value,
		label: value,
	}));
	const coatingOptions = (filterOptions?.coatings ?? []).map((value) => ({
		value,
		label: value,
	}));
	const buildingOptions = (buildings ?? []).map((building) => ({
		value: building.id,
		label: building.name,
	}));
	const selectedBuildingNames = buildingOptions
		.filter((option) => applied.buildings.includes(option.value))
		.map((option) => option.label);

	const rangeError =
		isError &&
		typeof error === 'object' &&
		error !== null &&
		'status' in error &&
		error.status === 400
			? String(
					(error as { data?: { detail?: string } }).data?.detail ??
						'This date range cannot be run.'
				)
			: null;

	const apply = () => {
		if (formInvalid) return;
		setApplied(draft);
		setSearchParams(filtersToSearchParams(draft));
	};

	const priorLabel = formatPriorRange(data?.prior_date_from, data?.prior_date_to);

	return (
		<>
			<Header
				title="Shop Performance"
				breadcrumbItems={[
					{ title: 'Admin' },
					{ title: 'Reports' },
					{ title: 'Shop Performance' },
				]}
				showOnClickButton={Boolean(data) && !dirty}
				buttonText="Print"
				icon={<PrinterOutlined />}
				handleOnClick={() => window.print()}
			/>

			<Space direction="vertical" size="large" className="w-full">
				<Text type="secondary">
					Sales uses jobs received in the date range. Revenue uses jobs whose
					first delivered-in-full date falls in that range. A job can appear in
					both. Cycle times always start from received date and compare to the
					same-length period immediately before.
				</Text>

				<Card styles={{ body: { padding: 20 } }}>
					<div className="flex flex-col gap-5">
						<section>
							<div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
								Period and site
							</div>
							<div className="grid grid-cols-1 gap-3 md:grid-cols-12">
								<div className="md:col-span-5">
									<FieldLabel>Date range</FieldLabel>
									<RangePicker
										value={toRange(draft)}
										allowClear={false}
										disabledDate={(current) =>
											Boolean(current && current.isAfter(dayjs().tz(CHICAGO), 'day'))
										}
										onChange={(range) => {
											if (!range?.[0] || !range[1]) return;
											setDraft((prev) => ({
												...prev,
												dateFrom: range[0]!.tz(CHICAGO).format('YYYY-MM-DD'),
												dateTo: range[1]!.tz(CHICAGO).format('YYYY-MM-DD'),
											}));
										}}
										className="w-full"
									/>
								</div>
								<div className="md:col-span-4">
									<FieldLabel>Manufacturing method</FieldLabel>
									<SearchableMulti
										placeholder="All methods"
										value={draft.methods}
										options={methodOptions}
										onChange={(methods) => setDraft((prev) => ({ ...prev, methods }))}
									/>
								</div>
								<div className="md:col-span-3">
									<FieldLabel>Site</FieldLabel>
									<SearchableMulti
										placeholder="All sites"
										value={draft.buildings}
										options={buildingOptions}
										onChange={(next) => setDraft((prev) => ({ ...prev, buildings: next }))}
									/>
								</div>
							</div>
						</section>

						<section>
							<div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
								Job mix
							</div>
							<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
								<div>
									<FieldLabel>Material group</FieldLabel>
									<SearchableMulti
										placeholder="All groups"
										value={draft.materials}
										options={materialOptions}
										onChange={(materials) => setDraft((prev) => ({ ...prev, materials }))}
									/>
								</div>
								<div>
									<FieldLabel>Surface finish</FieldLabel>
									<SearchableMulti
										placeholder="All finishes"
										value={draft.finishes}
										options={finishOptions}
										onChange={(finishes) => setDraft((prev) => ({ ...prev, finishes }))}
									/>
								</div>
								<div>
									<FieldLabel>Coating</FieldLabel>
									<SearchableMulti
										placeholder="All coatings"
										value={draft.coatings}
										options={coatingOptions}
										onChange={(coatings) => setDraft((prev) => ({ ...prev, coatings }))}
									/>
								</div>
							</div>
						</section>

						<section className="flex flex-col gap-3 md:flex-row md:items-end">
							<div className="min-w-0 flex-1">
								<FieldLabel>Planned lead time (days)</FieldLabel>
								<BoundPair
									min={draft.leadTimeMin}
									max={draft.leadTimeMax}
									error={leadError}
									onMin={(leadTimeMin) => setDraft((prev) => ({ ...prev, leadTimeMin }))}
									onMax={(leadTimeMax) => setDraft((prev) => ({ ...prev, leadTimeMax }))}
								/>
								{leadError ? (
									<div className="mt-1 text-[11px] text-red-600">Min must be on or before max.</div>
								) : null}
							</div>
							<div className="min-w-0 flex-1">
								<FieldLabel>Quantity</FieldLabel>
								<BoundPair
									min={draft.qtyMin}
									max={draft.qtyMax}
									error={qtyError}
									onMin={(qtyMin) => setDraft((prev) => ({ ...prev, qtyMin }))}
									onMax={(qtyMax) => setDraft((prev) => ({ ...prev, qtyMax }))}
								/>
								{qtyError ? (
									<div className="mt-1 text-[11px] text-red-600">Min must be on or before max.</div>
								) : null}
							</div>
							<Button type="primary" onClick={apply} disabled={formInvalid} className="md:mb-0">
								Apply
							</Button>
						</section>
					</div>
				</Card>

				{dirty && (
					<Alert
						type="info"
						showIcon
						message="Filters changed — Apply to refresh."
					/>
				)}
				{windowError && (
					<Alert
						type="error"
						showIcon
						message="Date range must be at most 366 days, with the end on or after the start."
					/>
				)}
				{appliedInvalid && !windowError && (
					<Alert
						type="error"
						showIcon
						message="These filters cannot be run. Fix the date or min/max bounds and Apply."
					/>
				)}

				{rangeError && (
					<Alert
						type="error"
						showIcon
						message={rangeError}
						action={
							<Button size="small" onClick={() => refetch()}>
								Retry
							</Button>
						}
					/>
				)}
				{isError && !rangeError && (
					<Alert
						type="error"
						showIcon
						message="The report could not be loaded."
						action={
							<Button size="small" onClick={() => refetch()}>
								Retry
							</Button>
						}
					/>
				)}

				<Spin spinning={isFetching}>
					{data && (
						<Space direction="vertical" size="large" className="w-full">
							<Row gutter={16}>
								<Col xs={24} lg={12}>
									<MoneyCard
										title="Sales (received in range)"
										block={data.sales}
										emptyText="No jobs received in this range"
										showMoney={data.include_revenue}
										moneyLabel="Sales"
										costFootnote={data.job_cost_footnote}
									/>
								</Col>
								<Col xs={24} lg={12}>
									<MoneyCard
										title="Revenue (delivered in full in range)"
										block={data.revenue}
										emptyText="None delivered in full in this range"
										showMoney={data.include_revenue}
										moneyLabel="Revenue"
										costFootnote={data.job_cost_footnote}
									/>
								</Col>
							</Row>

							<Card
								title="Other material costs"
								extra={<Text type="secondary">{data.other_costs.footnote}</Text>}
							>
								{data.other_costs.by_code.length === 0 ? (
									<Empty
										description="No correction costs logged."
										image={Empty.PRESENTED_IMAGE_SIMPLE}
									/>
								) : (
									<div className="flex flex-wrap gap-x-10 gap-y-3">
										{data.include_revenue && (
											<div>
												<div className="text-[11px] uppercase tracking-wide text-slate-500">
													Total
												</div>
												<div className="text-xl font-semibold tabular-nums">
													{formatDollars(data.other_costs.total)}
												</div>
											</div>
										)}
										{data.other_costs.by_code.map((row) => (
											<div key={row.code}>
												<div className="text-[11px] uppercase tracking-wide text-slate-500">
													{row.label}
												</div>
												<div className="text-lg tabular-nums">
													{data.include_revenue ? formatDollars(row.cost) : row.code}
												</div>
											</div>
										))}
									</div>
								)}
							</Card>

							<CyclePanel
								title="Based on received date"
								emptyText="No jobs received in this range"
								section={data.by_received}
								priorLabel={priorLabel}
							/>
							<CyclePanel
								title="Based on delivered date"
								emptyText="None delivered in full in this range"
								section={data.by_delivered}
								priorLabel={priorLabel}
							/>
						</Space>
					)}
				</Spin>
			</Space>

			{data && (
				<PrintableReport
					data={data}
					filters={applied}
					buildingNames={selectedBuildingNames}
				/>
			)}
		</>
	);
};

export default ShopPerformance;
